import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Play, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import {
  adresNagrania,
  adresWideo,
  czasNagrania,
  dlugoscNagrania,
  fetchNagrania,
  fetchUstawieniaNagran,
  KODY_NAGRAN,
  fetchKatalogiNagran,
  kodyDoPokazania,
  MAKS_LIMIT_MB,
  MAKS_USUN_PO_DNIACH,
  MAKS_OKRES_MIES,
  przelaczKod,
  rozmiarKatalogu,
  rozmiarMb,
  zapiszUstawieniaNagran,
  type Nagranie,
  type UstawieniaNagran,
} from '@/lib/nagrania-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  NAGRANIA Z KAMERY SPS (06.10.2026) — wrzutnia u MJM/Gestamp.
 *
 *  ⚠️ ODTWARZAMY KOPIĘ, POBIERAMY ORYGINAŁ. SPS zapisuje AVI z MPEG-4 Part 2, którego przeglądarka
 *  nie odtworzy — instancja robi obok kopię MP4 (zwykle w kilka sekund po przyjęciu pliku) i dopiero
 *  wtedy pojawia się „Odtwórz". Pobranie zawsze daje oryginał z maszyny.
 *
 *  ⚠️ CZASY SĄ ŚCIENNE AUTOMATU, bez przeliczania — tak samo jak godzina wydania na ekranie
 *  transakcji, więc wrzut z 21:57 na automacie jest 21:57 także tutaj.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const WARIANT_STANU: Record<Nagranie['stan'], 'success' | 'neutral' | 'warning'> = {
  WYSLANE: 'success',
  OCZEKUJE: 'neutral',
  ZA_DUZE: 'warning',
};

const RODZAJ_KODU = new Map(KODY_NAGRAN.map((k) => [k.kod, k.rodzaj]));

/** Dzień w strefie przeglądarki, `RRRR-MM-DD`. */
const dzien = (d: Date) => d.toLocaleDateString('sv-SE');
const przesun = (d: string, dni: number) => {
  const x = new Date(`${d}T12:00:00`);
  x.setDate(x.getDate() + dni);
  return dzien(x);
};

export function Nagrania() {
  const { t } = useI18n();
  const { groupId } = useIslands();
  const [doDnia, setDoDnia] = useState(() => dzien(new Date()));
  const [od, setOd] = useState(() => przesun(dzien(new Date()), -30));
  const [automat, setAutomat] = useState('');
  const [ustawienia, setUstawienia] = useState(false);
  const [odtwarzane, setOdtwarzane] = useState<Nagranie | null>(null);

  const dane = useQuery({
    queryKey: ['nagrania', groupId, od, doDnia],
    queryFn: () => fetchNagrania(groupId!, { od, do: doDnia }),
    enabled: !!groupId,
  });

  const automaty = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of dane.data?.nagrania ?? []) m.set(n.automat.id, n.automat.nazwa);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [dane.data]);
  const wiersze = (dane.data?.nagrania ?? []).filter((n) => automat === '' || n.automat.id === automat);

  if (!groupId) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3 text-sm">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('nagr.od')}
          <Input type="date" className="h-8 w-40" value={od} max={doDnia} onChange={(e) => setOd(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('nagr.do')}
          <Input type="date" className="h-8 w-40" value={doDnia} min={od} onChange={(e) => setDoDnia(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {t('nagr.automat')}
          <Select className="h-8 w-56" value={automat} onChange={(e) => setAutomat(e.target.value)}>
            <option value="">{t('nagr.wszystkie')}</option>
            {automaty.map(([id, nazwa]) => (
              <option key={id} value={id}>
                {nazwa}
              </option>
            ))}
          </Select>
        </label>
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => setUstawienia((v) => !v)}>
          <Settings2 />
          {t('nagr.ustawienia')}
        </Button>
      </div>

      {ustawienia && <UstawieniaNagranPanel groupId={groupId} />}

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-normal">{t('nagr.kolKiedy')}</th>
              <th className="px-3 py-2 font-normal">{t('nagr.kolAutomat')}</th>
              <th className="px-3 py-2 font-normal">{t('nagr.kolOperacja')}</th>
              <th className="px-3 py-2 font-normal">{t('nagr.kolOsoba')}</th>
              <th className="px-3 py-2 font-normal">{t('nagr.kolTowary')}</th>
              <th className="px-3 py-2 text-right font-normal">{t('nagr.kolDlugosc')}</th>
              <th className="px-3 py-2 text-right font-normal">{t('nagr.kolRozmiar')}</th>
              <th className="px-3 py-2 font-normal">{t('nagr.kolStan')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {wiersze.map((n) => (
              <tr key={n.id} className="border-t border-border/50 align-top">
                <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">{czasNagrania(n.start)}</td>
                <td className="px-3 py-1.5">{n.automat.nazwa}</td>
                <td className="px-3 py-1.5">
                  <span className="font-mono">{n.kody.join(', ')}</span>
                  {RODZAJ_KODU.has(n.kody[0] ?? '') && (
                    <span className="text-muted-foreground"> · {t(`nagr.rodzaj.${RODZAJ_KODU.get(n.kody[0]!)}`)}</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {n.osoba ? (
                    <>
                      {n.osoba.nazwa ?? '—'}
                      {n.osoba.numer && <span className="ml-1 font-mono text-xs text-muted-foreground">{n.osoba.numer}</span>}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {n.towary.length === 0
                    ? '—'
                    : n.towary.map((w, i) => (
                        <div key={`${i}-${w.numer ?? ''}`} className="whitespace-nowrap">
                          <span className="font-mono text-xs">{w.numer ?? '—'}</span>
                          {w.nazwa && <span className="text-muted-foreground"> {w.nazwa}</span>}
                          <span className="tabular-nums"> × {w.ilosc}</span>
                        </div>
                      ))}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {dlugoscNagrania(n.sekund) ?? t('nagr.sek').replace('{n}', String(n.sekund))}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{rozmiarMb(n.rozmiar)} MB</td>
                <td className="px-3 py-1.5">
                  <Badge variant={WARIANT_STANU[n.stan]}>{t(`nagr.stan.${n.stan}`)}</Badge>
                </td>
                <td className="px-3 py-1.5 text-right">
                  {n.stan === 'WYSLANE' && (
                    <div className="flex flex-col items-end gap-1">
                      {n.konwersja === 'GOTOWE' ? (
                        <button
                          type="button"
                          onClick={() => setOdtwarzane(n)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <Play className="size-3.5" />
                          {t('nagr.odtworz')}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {n.konwersja === 'BLAD' ? t('nagr.bezPodgladu') : t('nagr.przygotowuje')}
                        </span>
                      )}
                      <a
                        href={adresNagrania(groupId, n.id)}
                        download
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <Download className="size-3.5" />
                        {t('nagr.pobierz')}
                      </a>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {dane.isLoading && (
          <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        )}
        {dane.error && (
          <p className="p-4 text-sm text-destructive">
            {dane.error instanceof ApiError ? dane.error.message : t('common.error')}
          </p>
        )}
        {dane.data && wiersze.length === 0 && <p className="p-4 text-sm text-muted-foreground">{t('nagr.brak')}</p>}
      </div>

      {odtwarzane && <Odtwarzacz groupId={groupId} nagranie={odtwarzane} onClose={() => setOdtwarzane(null)} />}
    </div>
  );
}

/**
 * ODTWARZACZ NAGRANIA — kopia MP4 z instancji, z przewijaniem; obok to, czego dotyczy nagranie.
 *
 * ⚠️ OSOBA I TOWARY Z CHWILI WYDANIA, nie z kartoteki: nagranie ma pokazywać, kto wrzucał i co
 * zgłosił na automacie — a plastik bywa potem przekazany komuś innemu.
 */
function Odtwarzacz({ groupId, nagranie: n, onClose }: { groupId: string; nagranie: Nagranie; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`${czasNagrania(n.start)} · ${n.automat.nazwa}`}
        description={`${n.kody.join(', ')}${RODZAJ_KODU.has(n.kody[0] ?? '') ? ` · ${t(`nagr.rodzaj.${RODZAJ_KODU.get(n.kody[0]!)}`)}` : ''}`}
        className="w-[min(52rem,calc(100vw-2rem))]"
      >
        <video
          key={n.id}
          src={adresWideo(groupId, n.id)}
          controls
          autoPlay
          playsInline
          className="aspect-[4/3] w-full rounded-md bg-black"
        />
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3 text-sm">
          <div className="space-y-1">
            <p>
              {n.osoba?.nazwa ?? '—'}
              {n.osoba?.numer && <span className="ml-1 font-mono text-xs text-muted-foreground">{n.osoba.numer}</span>}
            </p>
            {n.towary.map((w, i) => (
              <p key={`${i}-${w.numer ?? ''}`} className="text-xs">
                <span className="font-mono">{w.numer ?? '—'}</span>
                {w.nazwa && <span className="text-muted-foreground"> {w.nazwa}</span>}
                <span className="tabular-nums"> × {w.ilosc}</span>
              </p>
            ))}
          </div>
          <a
            href={adresNagrania(groupId, n.id)}
            download
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Download className="size-3.5" />
            {t('nagr.pobierzOryginal')}
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * USTAWIENIA NAGRAŃ WYSPY — okres, limit pliku i które operacje pobierać.
 *
 * ⚠️ „POBIERAJ" I „USUŃ Z MASZYNY" TO DWIE RÓŻNE RZECZY: odznaczony „pobieraj" znaczy tylko „nie wysyłaj
 * na instancję", plik zostaje w katalogu SPS. Kasuje wyłącznie „usuń z maszyny", po „po dniach"
 * (od 12.10.2026, prośba Kamila; decyzja usera „kasuj po N dniach wprost", domyślnie nic).
 */
function UstawieniaNagranPanel({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const dane = useQuery({ queryKey: ['nagrania-ustawienia', groupId], queryFn: () => fetchUstawieniaNagran(groupId) });
  const [u, setU] = useState<UstawieniaNagran | null>(null);
  const [zapisuje, setZapisuje] = useState(false);

  useEffect(() => {
    if (dane.data) setU(dane.data);
  }, [dane.data]);

  if (dane.error) {
    return (
      <p className="rounded-lg border border-border bg-card p-3 text-sm text-destructive">
        {dane.error instanceof ApiError ? dane.error.message : t('common.error')}
      </p>
    );
  }
  if (u === null) {
    return (
      <p className="flex items-center gap-2 rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('common.loading')}
      </p>
    );
  }

  const poprawne =
    Number.isInteger(u.okresMies) &&
    u.okresMies >= 1 &&
    u.okresMies <= MAKS_OKRES_MIES &&
    Number.isInteger(u.limitMb) &&
    u.limitMb >= 1 &&
    u.limitMb <= MAKS_LIMIT_MB &&
    Number.isInteger(u.usunPoDniach) &&
    u.usunPoDniach >= 1 &&
    u.usunPoDniach <= MAKS_USUN_PO_DNIACH;
  const zmienione = JSON.stringify(u) !== JSON.stringify(dane.data);

  const zapisz = async (): Promise<void> => {
    setZapisuje(true);
    try {
      await zapiszUstawieniaNagran({ groupId, ...u });
      toast.success(t('nagr.zapisano'));
      await qc.invalidateQueries({ queryKey: ['nagrania-ustawienia', groupId] });
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {t('nagr.okres')}
          <Input
            type="number"
            className="h-8 w-20"
            min={1}
            max={MAKS_OKRES_MIES}
            value={u.okresMies}
            onChange={(e) => setU({ ...u, okresMies: Number(e.target.value) })}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {t('nagr.limit')}
          <Input
            type="number"
            className="h-8 w-20"
            min={1}
            max={MAKS_LIMIT_MB}
            value={u.limitMb}
            onChange={(e) => setU({ ...u, limitMb: Number(e.target.value) })}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {t('nagr.usunPoDniach')}
          <Input
            type="number"
            className="h-8 w-20"
            min={1}
            max={MAKS_USUN_PO_DNIACH}
            value={u.usunPoDniach}
            onChange={(e) => setU({ ...u, usunPoDniach: Number(e.target.value) })}
          />
        </label>
      </div>

      <table className="border-collapse text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-6 font-normal">{t('nagr.kolOperacja')}</th>
            <th className="px-3 py-1 text-center font-normal">{t('nagr.pobieraj')}</th>
            <th className="px-3 py-1 text-center font-normal">{t('nagr.usunZMaszyny')}</th>
          </tr>
        </thead>
        <tbody>
          {kodyDoPokazania(u).map((k) => (
            <tr key={k.kod} className="border-t border-border/50">
              <td className="py-1 pr-6">
                <span className="inline-block w-10 font-mono">{k.kod}</span>
                <span className="text-muted-foreground">{t(`nagr.rodzaj.${k.rodzaj}`)}</span>
              </td>
              <td className="px-3 py-1 text-center">
                <input
                  type="checkbox"
                  aria-label={`${t('nagr.pobieraj')} ${k.kod}`}
                  checked={u.pobierane.includes(k.kod)}
                  onChange={() => setU({ ...u, pobierane: przelaczKod(u.pobierane, k.kod) })}
                />
              </td>
              <td className="px-3 py-1 text-center">
                <input
                  type="checkbox"
                  aria-label={`${t('nagr.usunZMaszyny')} ${k.kod}`}
                  checked={u.usuwane.includes(k.kod)}
                  onChange={() => setU({ ...u, usuwane: przelaczKod(u.usuwane, k.kod) })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ⚠️ Kasowanie na maszynie jest nieodwracalne — to ma być widać przy przycisku, a nie w instrukcji. */}
      {u.usuwane.length > 0 && (
        <p className="text-xs text-[var(--warning)]">
          {t('nagr.usunOstrzezenie').replace('{kody}', u.usuwane.join(', ')).replace('{n}', String(u.usunPoDniach))}
        </p>
      )}

      <Button size="sm" disabled={!zmienione || !poprawne || zapisuje} onClick={() => void zapisz()}>
        {zapisuje && <Loader2 className="animate-spin" />}
        {t('common.save')}
      </Button>

      <KatalogiNaMaszynach groupId={groupId} />
    </section>
  );
}

/**
 * KATALOGI NAGRAŃ NA MASZYNACH (12.10.2026) — zamówione razem z kasowaniem: „żeby było jakieś info ile
 * teraz zajmuje ten katalog na poszczególnych maszynach". Liczby od agenta, mierzone co godzinę.
 */
function KatalogiNaMaszynach({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const katalogi = useQuery({ queryKey: ['nagrania-katalogi', groupId], queryFn: () => fetchKatalogiNagran(groupId) });
  if (!katalogi.data) return null;
  if (katalogi.data.length === 0) return <p className="text-xs text-muted-foreground">{t('nagr.katalogBrak')}</p>;
  return (
    <div className="overflow-x-auto">
      <p className="mb-1 text-xs font-medium">{t('nagr.katalogi')}</p>
      <table className="border-collapse text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="py-1 pr-6 font-normal">{t('nagr.kolAutomat')}</th>
            <th className="px-3 py-1 font-normal">{t('nagr.katalog')}</th>
            <th className="px-3 py-1 text-right font-normal">{t('nagr.plikow')}</th>
            <th className="px-3 py-1 text-right font-normal">{t('nagr.kolRozmiar')}</th>
            <th className="px-3 py-1 text-right font-normal">{t('nagr.usunieto')}</th>
            <th className="px-3 py-1 font-normal">{t('nagr.zmierzono')}</th>
          </tr>
        </thead>
        <tbody>
          {katalogi.data.map((k) => (
            <tr key={k.automat.id} className="border-t border-border/50">
              <td className="py-1 pr-6">
                VMID {k.automat.vmid} — {k.automat.nazwa}
              </td>
              <td className="px-3 py-1 font-mono">{k.katalog}</td>
              <td className="px-3 py-1 text-right tabular-nums">{k.plikow}</td>
              <td className="px-3 py-1 text-right tabular-nums">{rozmiarKatalogu(k.bajtow)}</td>
              <td className="px-3 py-1 text-right tabular-nums">{k.usunietych}</td>
              <td className="px-3 py-1 text-muted-foreground">{new Date(k.zmierzonoO).toLocaleString('pl-PL')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
